import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, Circle, Loader2, Play } from "lucide-react";
import ActiveCardShell from "../ActiveCardShell";
import { sleep, RunButton, useAlive } from "../phase2/shared";

// Generic "steps run one by one → outcome badge" card used for Phase 4 & 5 tools.
// steps: [{ label, detail, warn? }]
export default function SequenceCard({ id, kicker, title, Icon, runLabel, steps, outcome, stepMs = 650, header, onDone }) {
  const tid = id.replace(/cr$/i, "").toLowerCase();
  const [done, setDone] = useState(0);
  const [finished, setFinished] = useState(false);
  const [running, setRunning] = useState(false);
  const alive = useAlive();

  const run = async () => {
    setRunning(true); setDone(0); setFinished(false);
    for (let i = 1; i <= steps.length; i += 1) { await sleep(stepMs); if (!alive.current) return; setDone(i); }
    await sleep(500);
    if (!alive.current) return;
    setFinished(true);
    setRunning(false);
    onDone?.();
  };

  return (
    <ActiveCardShell id={id} kicker={kicker} title={title} Icon={Icon}
      actions={<RunButton tid={`veroogle-${tid}-run`} running={running} onClick={run} Icon={running ? Loader2 : Play} label={runLabel} />}>
      {header && <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-3">{header}</div>}
      <ul className="space-y-2">
        {steps.map((s, i) => {
          const ok = i < done;
          const active = running && i === done;
          const StatusIcon = ok ? (s.warn ? AlertTriangle : CheckCircle2) : active ? Loader2 : Circle;
          const tone = ok ? (s.warn ? "border-amber-400/50 bg-amber-500/10" : "border-emerald-500/30 bg-emerald-500/5") : "border-white/10 bg-navy-950/40";
          return (
            <li key={s.label} data-testid={`veroogle-${tid}-step-${i}`}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors duration-300 ${tone}`}>
              <StatusIcon className={`w-4 h-4 shrink-0 ${ok ? (s.warn ? "text-amber-300" : "text-emerald-400") : active ? "text-brand-cyan animate-spin" : "text-slate-600"}`} />
              <span className={`text-sm flex-1 ${ok ? "text-white" : "text-slate-500"}`}>{s.label}</span>
              <span className="text-xs text-slate-400 text-right hidden sm:inline">{ok ? s.detail : active ? "Working…" : ""}</span>
            </li>
          );
        })}
      </ul>
      {finished && (
        <div data-testid={`veroogle-${tid}-outcome`}
          className="mt-4 inline-flex max-w-full break-words px-4 py-2 rounded-lg bg-emerald-500 text-navy-950 font-mono text-xs sm:text-sm font-bold tracking-[0.14em] uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)] animate-in zoom-in-75 fade-in">
          {outcome}
        </div>
      )}
    </ActiveCardShell>
  );
}
